import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_12_MAGAZYN_ENERGII: ProgrammaticLandingPageConfig = {
  slug: 'magazyn-energii-12-kwh-cena',
  lang: 'pl',
  topicId: 'kwh-speicher-12',
  title: 'Magazyn energii 12 kWh: cena i wartości orientacyjne 2026',
  metaDescription:
    'Ile kosztuje magazyn energii 12 kWh? Dla kogo się nadaje, wartości orientacyjne ' +
    'autokonsumpcji i bezpłatne porównanie ofert na instalację + magazyn.',
  keywords: [
    'magazyn energii 12 kWh cena',
    'magazyn energii 12 kWh koszt',
    'bateria fotowoltaiczna 12 kWh',
    'ile kosztuje magazyn energii 12 kWh',
  ],
  eyebrow: 'Wielkość magazynu: 12 kWh',
  h1: 'Magazyn energii 12 kWh: cena i wartości orientacyjne',
  intro:
    'Magazyn energii 12 kWh pasuje do gospodarstw o nieco wyższym zużyciu niż standardowe, w ' +
    `połączeniu z referencyjną instalacją ${STORAGE_REFERENCE_KWP} kWp. Znajdziesz tu wartości ` +
    'orientacyjne dotyczące odpowiedniej wielkości gospodarstwa, autokonsumpcji z magazynem ' +
    'energii, a tuż poniżej kalkulator z już wypełnioną średnią ceną dla tego połączenia, ' +
    'obliczoną na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 12 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiedni jest magazyn energii 12 kWh?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 12 kWh nadaje się dla gospodarstw z podwyższonym zużyciem wieczorem ' +
        'lub okazjonalnym ładowaniem samochodu elektrycznego. Przy pompie ciepła lub regularnym ' +
        'ładowaniu samochodu elektrycznego zwykle lepiej pasuje 15 lub 20 kWh.',
    },
    { type: 'heading', text: 'Autokonsumpcja z magazynem energii' },
    {
      type: 'paragraph',
      text:
        `Bez magazynu udział autokonsumpcji w instalacji ${STORAGE_REFERENCE_KWP} kWp wynosi ` +
        `zwykle ok. ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %; z magazynem energii - w modelu ` +
        `obliczeniowym o ok. ${SELF_CONSUMPTION_BATTERY_BONUS * 100} punktów procentowych więcej, ` +
        `do ok. ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - dodatkowy profil ładowania samochodu ` +
        'elektrycznego może dalej podnosić ten udział, aż do granicy 85 %. Dokładne obliczenie dla ' +
        'Twojego zużycia daje kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Zasilanie awaryjne' },
    {
      type: 'paragraph',
      text:
        'Przy 12 kWh pojemność wielu skrzynek backup do zasilania awaryjnego wystarcza na wyraźnie ' +
        'większą część domu przez dłuższy czas - dokładny czas zależy od rzeczywistego zużycia ' +
        'podłączonych urządzeń. Czy i w jakim zakresie dana oferta obejmuje zasilanie awaryjne, ' +
        'zależy od konkretnej firmy - warto o to zapytać bezpośrednio.',
    },
    { type: 'heading', text: 'Koszty magazynu i dofinansowanie' },
    {
      type: 'paragraph',
      text:
        'Koszty magazynów energii mocno się różnią w zależności od producenta i złożoności ' +
        'montażu - badanie rynku z 2026 roku pokazuje przedział od ok. ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} do ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('pl-PL')} ` +
        '€ za kWh. W Niemczech istnieją regionalne dofinansowania specjalnie na magazyny energii ' +
        '(np. program kraju związkowego Berlin z dopłatą do magazynów energii); w Austrii system ' +
        'EAG dolicza 150 €/kWh za magazyn - dokładne obliczenie dla Twojego połączenia daje ' +
        'kalkulator poniżej.',
    },
  ],
  faq: [
    {
      q: 'Kiedy 12 kWh jest lepszym wyborem niż 10 kWh?',
      a: 'Przy podwyższonym zużyciu wieczorem lub gdy od czasu do czasu ładowany jest w domu samochód elektryczny, 12 kWh zwykle lepiej pokrywa dodatkowe zapotrzebowanie niż 10 kWh.',
    },
    {
      q: 'Czy 12 kWh opłaca się bez pompy ciepła i samochodu elektrycznego?',
      a: 'Zwykle nie jest optymalnie wykorzystywane - bez tych dużych odbiorników 10 kWh zwykle wystarcza dla większości gospodarstw domowych. Dokładną rekomendację daje kalkulator doboru instalacji.',
    },
    {
      q: 'Z jaką wielkością instalacji łączony jest zwykle magazyn 12 kWh?',
      a: `Na tej stronie jako punkt odniesienia przyjęto instalację ${STORAGE_REFERENCE_KWP} kWp, w praktyce często łączony też z 12 kWp - dokładną rekomendację dla Twojego dachu daje kalkulator doboru instalacji.`,
    },
    {
      q: 'Czy 12 kWh wystarczy na pełną samowystarczalność w nocy?',
      a: 'Tak, w większości gospodarstw domowych nawet przy zużyciu powyżej średniej. Dokładną ocenę dla Twojego rzeczywistego zużycia daje kalkulator doboru instalacji.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Magazyn energii 10 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-10-kwh-cena' },
    { label: 'Magazyn energii 15 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-15-kwh-cena' },
    { label: 'Instalacja fotowoltaiczna 12 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-12-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
